/**
 * Live Kuma health from Prometheus, via the @mgmt control plane (serviceApiV3,
 * base …/api/v3). Uptime-Kuma exports per-monitor metrics that Prometheus
 * scrapes; the gateway proxies instant/range PromQL at
 * `/monitoring/metrics/query[_range]`.
 *
 * Response shape — VERIFIED against @mgmt `modules/monitoring/controller.go` +
 * `pkg/prometheus/client.go`: the controller wraps Prometheus's `data` object in
 * `response.OK` → `{ status, data: { resultType, result } }`. So unwrap via
 * `res.data.data.result`. A vector sample is
 * `{ metric: { monitor_name, monitor_url, … }, value: [unixTs, "<number>"] }`.
 *
 * Metrics join the inventory list by the `monitor_name` label.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

interface PromVectorSample {
  metric: Record<string, string>;
  /** [ unix seconds, stringified number ] */
  value: [number, string];
}
interface PromVectorData {
  resultType: string;
  result: PromVectorSample[];
}
interface Envelope<T> {
  status?: number;
  data: T;
}

async function instantQuery(query: string): Promise<PromVectorSample[]> {
  const res = await serviceApiV3.get<Envelope<PromVectorData>>('/monitoring/metrics/query', {
    params: { query },
  });
  return res.data?.data?.result ?? [];
}

/** Live health for one monitor, joined by `monitor_name`. */
export interface KumaLiveMetric {
  /** monitor_status: true = up, false = down. */
  up?: boolean;
  /** monitor_response_time, milliseconds. */
  responseMs?: number;
  /** 24h uptime percentage — avg_over_time(monitor_status[24h])*100. */
  uptime24h?: number;
}

export type KumaMetricsByName = Record<string, KumaLiveMetric>;

/**
 * Live status + latency + 24h uptime for every monitor, indexed by
 * `monitor_name` for table joins. Best-effort: a Prometheus outage resolves to
 * an empty map (columns fall back to "—") and never blocks the inventory table.
 */
export function useKumaMetrics() {
  return useQuery<KumaMetricsByName>({
    queryKey: ['kuma', 'metrics'],
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const [status, responseTime, uptime] = await Promise.all([
        instantQuery('monitor_status'),
        instantQuery('monitor_response_time'),
        instantQuery('avg_over_time(monitor_status[24h])*100'),
      ]);
      const by: KumaMetricsByName = {};
      const patch = (name: string | undefined, p: KumaLiveMetric) => {
        if (!name) return;
        by[name] = { ...by[name], ...p };
      };
      for (const s of status) patch(s.metric.monitor_name, { up: Number(s.value[1]) === 1 });
      for (const r of responseTime) patch(r.metric.monitor_name, { responseMs: Math.round(Number(r.value[1])) });
      for (const u of uptime) patch(u.metric.monitor_name, { uptime24h: Number(u.value[1]) });
      return by;
    },
  });
}

/** One response-chart sample: `t` = unix seconds, `v` = response time (ms). */
export interface KumaStreamPoint {
  t: number;
  v: number;
}

/**
 * Everything the Kuma-style monitor detail view needs, packaged by the gateway
 * in one call. Mirrors @mgmt `StreamDetailResponse` (modules/monitoring/
 * stream_detail.go): all scalars are nullable pointers there, so they may be
 * `null` when Prometheus has no sample (e.g. cert days for a non-HTTPS monitor).
 */
export interface KumaStreamDetail {
  name: string;
  /** 1 = up, 0 = down (monitor_status). */
  status: number | null;
  /** Current response time (ms). */
  response_ms: number | null;
  /** 24h average response time (ms). */
  avg_response_ms_24h: number | null;
  uptime_24h: number | null;
  uptime_30d: number | null;
  uptime_1y: number | null;
  cert_days_remaining: number | null;
  response_series: KumaStreamPoint[];
}

/**
 * Packaged Kuma-style detail for one monitor (status, response, uptime windows,
 * cert, response chart). The handler answers with `response.OK` → `{ status,
 * data }` (same envelope as the other monitoring hooks), so unwrap `res.data.data`.
 */
export function useKumaStreamDetail(name: string | undefined) {
  return useQuery<KumaStreamDetail>({
    queryKey: ['kuma', 'stream-detail', name],
    enabled: !!name,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<KumaStreamDetail>>('/monitoring/streams/detail', {
        params: { name, minutes: 60, step: 60 },
      });
      return res.data.data;
    },
  });
}

/** Range presets for the response chart (matches the Kuma selector). */
export type KumaChartRange = 'recent' | '3h' | '6h' | '24h' | '1w';

/** Min/Avg/Max response-time bands over a range (`/monitoring/streams/chart`). */
export interface KumaStreamChart {
  name: string;
  range: string;
  min: KumaStreamPoint[];
  avg: KumaStreamPoint[];
  max: KumaStreamPoint[];
}

/**
 * Min/Avg/Max response-time bands for the Kuma-style chart, over a selectable
 * range. Mirrors @mgmt `ChartResponse` (modules/monitoring/chart.go) — unwrap
 * `res.data.data`. Each band is a list of `{t,v}` (t=unix seconds, v=ms).
 */
export function useKumaStreamChart(name: string | undefined, range: KumaChartRange = '24h') {
  return useQuery<KumaStreamChart>({
    queryKey: ['kuma', 'stream-chart', name, range],
    enabled: !!name,
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<KumaStreamChart>>('/monitoring/streams/chart', {
        params: { name, range },
      });
      return res.data.data;
    },
  });
}
